import { Crown } from 'lucide-react'
import { Button } from './button'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip'
import { useTranslation } from '@/hooks/useTranslation'

interface PremiumFeatureProps {
	children: React.ReactNode
	isLocked?: boolean
	message?: string
	showIcon?: boolean
	showTooltip?: boolean
	className?: string
}

export function PremiumFeature({
	children,
	isLocked = false,
	message,
	showIcon = true,
	showTooltip = true,
	className,
}: PremiumFeatureProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const defaultMessage = message || t('premiumFeature.defaultMessage')

	const content = (
		<div className={cn('relative', className)}>
			<div className={cn('transition-all duration-200', isLocked && 'opacity-60')}>
				{children}
			</div>
			{isLocked && showIcon && (
				<div className="absolute top-0 right-0 -mt-2 -mr-2 z-20">
					<div className="bg-yellow-100 rounded-full p-2 border border-yellow-200 shadow-sm hover:bg-yellow-200 transition-colors cursor-pointer">
						<Crown className="h-4 w-4 text-yellow-600" />
					</div>
				</div>
			)}
		</div>
	)

	if (!isLocked) {
		return content
	}

	const tooltipContent = (
		<div className="text-center max-w-xs">
			<div className="flex items-center justify-center mb-2">
				<Crown className="h-4 w-4 text-yellow-500 mr-1" />
				<span className="font-medium text-sm">{t('premiumFeature.proFeature')}</span>
			</div>
			<p className="text-sm mb-3 text-gray-600">{defaultMessage}</p>
			<Button
				size="sm"
				className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-3 py-1.5 h-auto"
				onClick={() => navigate('/settings?tab=billing')}>
				<Crown className="h-3 w-3 mr-1" />
				{t('premiumFeature.upgradeToPro')}
			</Button>
		</div>
	)

	if (!showTooltip) {
		return content
	}

	return (
		<div className={cn('relative', className)}>
			<div className={cn('transition-all duration-200', isLocked && 'opacity-60')}>
				{children}
			</div>
			{isLocked && showIcon && (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="absolute top-0 right-0 -mt-2 -mr-2 z-20">
								<div className="bg-yellow-100 rounded-full p-2 border border-yellow-200 shadow-sm hover:bg-yellow-200 transition-colors cursor-pointer">
									<Crown className="h-4 w-4 text-yellow-600" />
								</div>
							</div>
						</TooltipTrigger>
						<TooltipContent 
							side="top" 
							align="end"
							className="max-w-sm"
							sideOffset={8}
						>
							{tooltipContent}
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)}
		</div>
	)
}
